import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './MatchMarket.css';

/**
 * MatchMarket — parimutuell marknad: satsa tipspoäng på matchvinnaren.
 *
 * Alla insatser bildar potten; vinnarsidan delar förlorarnas poäng
 * proportionellt (huset tar inget). Multiplikatorn per sida är communityts
 * levande odds: pott/sidans insatser. Insatser tas emot tills matchen går
 * live (samma lås som tipsen); efter SERIES_END visas facit per insats.
 *
 * Monteras inne i MatchPrediction så marknaden följer med överallt där
 * tipskortet finns (lobby, live-hubben, matchsidan). Renderar null när
 * marknaden är avstängd (SiteConfig MARKET_ENABLED).
 *
 * @param {{matchId: number|string}} props Matchens id.
 * @returns {JSX.Element|null} Marknadssektionen.
 */
export default function MatchMarket({ matchId }) {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [pick, setPick] = useState(null);
  const [amount, setAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    fetch(`${API_URL}/api/predictions/market/by-match/${matchId}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setData(body); })
      .catch(() => {
        // tyst — marknaden är icke-kritisk för sidan
      });
  }, [matchId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!data || !data.enabled) return null;

  const { pools, locked, finished, min_stake: minStake, max_stake: maxStake } = data;
  const myStakes = data.my_stakes || [];
  const hasPool = pools.total > 0;
  // Marknaden erbjuds bara för 5v5 (turnering/liga från 2v2) — dölj
  // sektionen helt för andra format, utom när egna insatser redan finns
  // (facit får aldrig försvinna).
  const offered = data.offered !== false;
  if (!offered && myStakes.length === 0) return null;

  /**
   * Lägg insatsen på vald sida.
   */
  const placeStake = async () => {
    if (!pick || !amount) return;
    setPlacing(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/predictions/market/stake`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ match_id: matchId, pick, amount: Number(amount) }),
      });
      const body = await res.json();
      if (!res.ok) {
        const key = {
          insufficient_balance: 'market.errInsufficient',
          stake_out_of_bounds: 'market.errBounds',
          own_team_only: 'market.errOwnTeam',
          market_not_offered: 'market.errNotOffered',
        }[body.code];
        setMessage({ kind: 'error', text: key ? t(key) : (body.error || t('market.errGeneric')) });
      } else {
        setMessage({ kind: 'success', text: t('market.stakePlaced') });
        setAmount('');
        setPick(null);
        window.dispatchEvent(new CustomEvent('wallet-refresh'));
        load();
      }
    } catch {
      setMessage({ kind: 'error', text: t('market.errGeneric') });
    } finally {
      setPlacing(false);
    }
  };

  /**
   * Facit-etikett för en egen insats efter settlement.
   *
   * @param {object} stake Insatsrad från API:t.
   * @returns {JSX.Element} Status-span med färgklass.
   */
  const stakeResult = (stake) => {
    if (stake.payout === null) {
      return <span className="market-stake-result pending">⏳</span>;
    }
    if (stake.is_void) {
      return <span className="market-stake-result void">↩️ +{stake.payout}</span>;
    }
    if (stake.payout > 0) {
      return <span className="market-stake-result won">📈 +{stake.payout}</span>;
    }
    return <span className="market-stake-result lost">−{stake.amount}</span>;
  };

  const share = (side) => (hasPool ? Math.round((pools[side] / pools.total) * 100) : 50);

  const teamButton = (side, name) => {
    const mult = pools.multipliers[side];
    return (
      <button
        type="button"
        className={`market-side-btn ${pick === side ? 'selected' : ''}`}
        onClick={() => setPick(pick === side ? null : side)}
      >
        <span className="market-side-name">{name}</span>
        <span className="market-side-pool">
          🪙 {pools[side].toLocaleString(currentLocale())}
          {mult !== null && <span className="market-side-mult"> ×{mult}</span>}
        </span>
      </button>
    );
  };

  return (
    <div className="match-market">
      <div className="market-header">
        <span className="market-title">📈 {t('market.title')}</span>
        <span className="market-pot">
          {t('market.pot')}: 🪙 {pools.total.toLocaleString(currentLocale())}
        </span>
      </div>

      {hasPool && (
        <div className="market-pool-bar">
          <div className="market-pool-fill team1" style={{ '--share': `${share('team1')}%` }} />
        </div>
      )}

      {message && <div className={`market-message ${message.kind}`}>{message.text}</div>}

      {!locked && user && offered && (
        <>
          <div className="market-sides">
            {teamButton('team1', data.team1_name)}
            {teamButton('team2', data.team2_name)}
          </div>
          {pick && (
            <div className="market-stake-form">
              <input
                className="market-amount-input"
                type="number"
                min={minStake}
                max={maxStake > 0 ? maxStake : undefined}
                placeholder={t('market.amountPlaceholder', { min: minStake })}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              {amount && Number(amount) >= minStake && (
                <span className="market-payout-preview">
                  {t('market.payoutPreview', {
                    payout: Math.floor(
                      Number(amount) * (pools.total + Number(amount))
                      / (pools[pick] + Number(amount)),
                    ),
                  })}
                </span>
              )}
              <button
                type="button"
                className="market-stake-btn"
                disabled={placing || !amount || Number(amount) < minStake}
                onClick={placeStake}
              >
                {placing ? t('market.placing') : t('market.placeStake')}
              </button>
            </div>
          )}
          <p className="market-hint">
            {t('market.hint', { min: minStake })}
            {maxStake > 0 && ` ${t('market.hintMax', { max: maxStake })}`}
          </p>
        </>
      )}

      {!locked && !user && (
        <p className="market-hint">{t('market.loginToStake')}</p>
      )}

      {locked && !finished && (
        <p className="market-hint">{t('market.lockedHint')}</p>
      )}

      {myStakes.length > 0 && (
        <div className="market-my-stakes">
          {myStakes.map((stake) => (
            <div key={stake.id} className="market-my-stake-row">
              <span className="market-my-stake-pick">
                {stake.pick === 'team1' ? data.team1_name : data.team2_name}
              </span>
              <span className="market-my-stake-amount">🪙 {stake.amount}</span>
              {stakeResult(stake)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
